import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MaterialModule } from '@app/shared/material.module';
import { SiteService } from '@app/v2/services/site/site.service';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import { CardMode } from '@components/card/card.component';
import { MdsCheckboxComponent } from '@medpacesoftwaredevelopment/designsystem';
import { Site } from '@models/site';
import { SiteStateService } from '@services/state-management/site-state.service';
import { rxjsComputed, rxjsEffect } from '@utility/utility';
import { combineLatest, filter, map, of, switchMap, tap } from 'rxjs';
import { FormErrorsComponent } from '../../form-errors/form-errors.component';
import { SiteServicesPaymentOptionsFormGroup } from './site-services-payment-options.form';

type PaymentControlsState =
    | 'viewMode'
    | 'paymentDisabled'
    | 'countryNotSupported'
    | 'payQuickerLockedFromApi'
    | 'payQuickerSelected'
    | 'editable';

@Component({
    selector: 'medpace-site-services-payment-options',
    imports: [MdsCheckboxComponent, AsyncPipe, FormErrorsComponent, MaterialModule],
    templateUrl: './site-services-payment-options.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SiteServicesPaymentOptionsComponent {
    userService = inject(UserServiceV2);
    siteStateService = inject(SiteStateService);
    siteService = inject(SiteService);
    elevatedAccess = this.userService.signalPermissions.site.access.elevated;
    mode = input.required<CardMode>();
    mode$ = toObservable(this.mode);
    formGrp = input.required<SiteServicesPaymentOptionsFormGroup>();
    formGrp$ = toObservable(this.formGrp);
    site = input<Site>();
    siteCountry = input<string>();
    site$ = toObservable(this.site).pipe(filter(Boolean));
    isPaymentEnabled = input<boolean>(false);
    isPaymentEnabled$ = toObservable(this.isPaymentEnabled);
    siteCountry$ = toObservable(this.siteCountry);

    patch$ = rxjsEffect(() =>
        combineLatest({
            site: this.site$,
            formGroup: this.formGrp$,
        }).pipe(
            tap(({ formGroup, site }) => {
                formGroup.patchValue({
                    prePaidDebitCardControl: site.payment.credit,
                    checkControl: site.payment.check,
                    reimburseControl: site.payment.reimburse,
                    wireTransferControl: site.payment.wireTransfer,
                    collectBankDetailsControl: site.payment.collectBankDetails,
                    payQuickerControl: site.payment.payQuicker,
                });
            })
        )
    );

    prepaid$ = rxjsComputed(() =>
        this.formGrp$.pipe(
            switchMap((formGroup) => formGroup.controls.prePaidDebitCardControl.valueChangesStartWithCurrentValue())
        )
    );
    check$ = rxjsComputed(() =>
        this.formGrp$.pipe(
            switchMap((formGroup) => formGroup.controls.checkControl.valueChangesStartWithCurrentValue())
        )
    );
    wireTransfer$ = rxjsComputed(() =>
        this.formGrp$.pipe(
            switchMap((formGroup) => formGroup.controls.wireTransferControl.valueChangesStartWithCurrentValue())
        )
    );
    reimburse$ = rxjsComputed(() =>
        this.formGrp$.pipe(
            switchMap((formGroup) => formGroup.controls.reimburseControl.valueChangesStartWithCurrentValue())
        )
    );
    payQuicker$ = rxjsComputed(() =>
        this.formGrp$.pipe(
            switchMap((formGroup) => formGroup.controls.payQuickerControl.valueChangesStartWithCurrentValue())
        )
    );
    isAnyOptionSelected$ = rxjsComputed(() =>
        combineLatest({
            prepaid: this.prepaid$.source$,
            check: this.check$.source$,
            wireTransfer: this.wireTransfer$.source$,
            reimburse: this.reimburse$.source$,
            payQuicker: this.payQuicker$.source$,
        }).pipe(
            map(({ check, prepaid, reimburse, wireTransfer, payQuicker }) => {
                return check || prepaid || reimburse || wireTransfer || payQuicker;
            })
        )
    );

    payQuickerCountrySupported$ = rxjsComputed(() =>
        this.siteCountry$.pipe(
            switchMap((siteCountry) =>
                siteCountry ? this.siteService.isPayQuickerCountrySupported(siteCountry) : of(false)
            )
        )
    );

    // Discriminates every combination that affects which payment controls are enabled/disabled.
    paymentControlsState$ = rxjsComputed(() =>
        combineLatest({
            supported: this.payQuickerCountrySupported$.source$,
            payQuicker: this.payQuicker$.source$,
            mode: this.mode$,
            site: this.site$,
            isPaymentEnabled: this.isPaymentEnabled$,
        }).pipe(
            map(({ supported, payQuicker, mode, site, isPaymentEnabled }): PaymentControlsState => {
                if (mode !== 'edit') return 'viewMode';

                // Edit mode
                if (!isPaymentEnabled) return 'paymentDisabled';
                if (!supported) return 'countryNotSupported';
                if (payQuicker) return site.payment.payQuicker ? 'payQuickerLockedFromApi' : 'payQuickerSelected';
                return 'editable';
            })
        )
    );

    payQuickerTooltip = toSignal(
        this.paymentControlsState$.source$.pipe(
            map((state) =>
                state === 'countryNotSupported' ? 'PayQuicker is not supported for sites in this country' : ''
            )
        ),
        { initialValue: '' }
    );

    disableControls$ = rxjsEffect(() =>
        combineLatest({
            state: this.paymentControlsState$.source$,
            formGroup: this.formGrp$,
        }).pipe(
            tap(({ state, formGroup }) => {
                const otherControls = [
                    formGroup.controls.prePaidDebitCardControl,
                    formGroup.controls.checkControl,
                    formGroup.controls.wireTransferControl,
                    formGroup.controls.reimburseControl,
                ];

                switch (state) {
                    case 'viewMode':
                        // Read-only display — keep whatever value patch$ loaded from the API.
                        formGroup.controls.payQuickerControl.disable({ persistent: true });
                        otherControls.forEach((c) => c.disable({ persistent: true }));
                        formGroup.controls.collectBankDetailsControl.disable({ persistent: true });
                        break;
                    case 'paymentDisabled':
                        formGroup.controls.payQuickerControl.patchValue(false, { emitEvent: false });
                        formGroup.controls.payQuickerControl.disable({ persistent: true });
                        otherControls.forEach((c) => c.disable({ persistent: true }));
                        formGroup.controls.collectBankDetailsControl.disable({ persistent: true });
                        break;
                    case 'countryNotSupported':
                        formGroup.controls.payQuickerControl.patchValue(false);
                        formGroup.controls.payQuickerControl.disable({ persistent: true });
                        otherControls.forEach((c) => c.enable({ breakPersistence: true }));
                        formGroup.controls.collectBankDetailsControl.enable({ breakPersistence: true });
                        break;
                    case 'payQuickerLockedFromApi':
                        // Site loaded from API with payQuicker=true — keep it checked, disable all controls
                        formGroup.controls.payQuickerControl.patchValue(true);
                        formGroup.controls.payQuickerControl.disable({ persistent: true });
                        otherControls.forEach((c) => c.disable({ persistent: true }));
                        formGroup.controls.collectBankDetailsControl.disable({ persistent: true });
                        break;
                    case 'payQuickerSelected':
                        // User selected payQuicker during editing — keep payQuicker editable
                        formGroup.controls.payQuickerControl.enable({ breakPersistence: true });
                        otherControls.forEach((c) => {
                            c.patchValue(false);
                            c.disable({ persistent: true });
                        });
                        formGroup.controls.collectBankDetailsControl.patchValue(false);
                        formGroup.controls.collectBankDetailsControl.disable({ persistent: true });
                        break;
                    case 'editable':
                        formGroup.controls.payQuickerControl.enable({ breakPersistence: true });
                        otherControls.forEach((c) => c.enable({ breakPersistence: true }));
                        formGroup.controls.collectBankDetailsControl.enable({ breakPersistence: true });
                        break;
                }
            })
        )
    );

    push(site: Site) {
        const value = this.formGrp().getRawValue();
        site.payment.credit = value.prePaidDebitCardControl ?? false;
        site.payment.check = value.checkControl ?? false;
        site.payment.wireTransfer = value.wireTransferControl ?? false;
        site.payment.collectBankDetails = (value.wireTransferControl && value.collectBankDetailsControl) ?? false;
        site.payment.reimburse = value.reimburseControl ?? false;
        site.payment.payQuicker = value.payQuickerControl ?? false;

        return site;
    }
}
